/*
 * @author Andrey Patyuchenko
 * @email  apatyuchenko@atlassian.com
 * @email  andrey.patyuchenko@gmail.com
 * @date   3/3/22
 */

import React from "react";
import {Autocomplete, CircularProgress, TextField} from "@mui/material";
import {useQuery} from "urql";
import {EmployeesSearchQuery, EmployeeSearchVars, EmployeesQueryResponse} from "../graphql/queries/employees";

interface Props {
	label?: string
	multiple?: boolean
	values?: string[]
	defaultNames?: Record<string, string>
	onChange: (options: string[]) => void
	disabled?: boolean
}

export default function EmployeesControl({ label, values, onChange, defaultNames, multiple, disabled }: Props) {

	const [search, setSearch] = React.useState("");
	const [names, setNames] = React.useState(defaultNames ?? {});

	const [namesResult] = useQuery<EmployeesQueryResponse, EmployeeSearchVars>({
		query: EmployeesSearchQuery,
		variables: { idIn: values },
		pause: !values || values.length === 0 || values.every(v => v in names),
	});

	const [autoCompleteResult] = useQuery<EmployeesQueryResponse, EmployeeSearchVars>({
		query: EmployeesSearchQuery,
		variables: { search },
		pause: search.length < 3,
	});

	React.useEffect(() => {
		if (namesResult.data?.employees.items) {
			const nNames: Record<string, string> = {};
			namesResult.data.employees.items.forEach(e => nNames[e.id] = e.displayName);
			setNames((names) => ({ ...names, ...nNames }));
		}
	}, [namesResult])

	React.useEffect(() => {
		if (autoCompleteResult.data?.employees.items) {
			const acNames: Record<string, string> = {};
			autoCompleteResult.data.employees.items.forEach(e => acNames[e.id] = e.displayName);
			setNames((names) => ({ ...names, ...acNames }));
		}
	}, [autoCompleteResult]);

	const { data, fetching } = autoCompleteResult;

	return (
		<Autocomplete
			multiple
			limitTags={multiple === false ? 1 : undefined}
			options={[
				...(values||[]),
				...(data?.employees.items.map(e => e.id as string) || [])
			]}
			disabled={disabled}
			loading={fetching}
			defaultValue={values === undefined ? [] : undefined}
			value={values}
			onChange={(event: object, v) => {
				if (v) {
					onChange(v);
					setSearch("");
				}
			}}
			ChipProps={{
				size: "small"
			}}
			clearOnEscape
			filterSelectedOptions
			fullWidth
			onInputChange={(_, value) => {
				setSearch(value);
			}}
			filterOptions={x => x}
			isOptionEqualToValue={(option, value) => option === value}
			getOptionLabel={(option) => {
				return option in names ? names[option] + " (" + option + ")" : option;
			}}
			noOptionsText={search.length === 0 ? "Start typing to see options..." : undefined}
			renderInput={(params) => (
				<TextField
					{...params}
					variant="standard"
					label={label}
					InputProps={{
						...params.InputProps,
						endAdornment: (
							<React.Fragment>
							{fetching ? <CircularProgress color="inherit" size={20} /> : null}
							{params.InputProps.endAdornment}
								</React.Fragment>
						),
					}}
				/>
				)}
		/>
	);

}